import { useEffect, useState } from "react";
import { LayoutComponent } from "../LayoutComponent";
import {
  Cell,
  Column,
  Row,
  TableView,
  TableBody,
  TableHeader,
  Flex,
  Heading,
  Content,
  View,
  Link,
  ProgressCircle,
  DialogContainer,
  Dialog,
} from "@adobe/react-spectrum";
import BillingLineAddEdit from "./BillingLineAddEdit";
import BarcodeScannerModal from "./BarcodeScannerModal";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { ListProjectItemRequest } from "../../../../services/soap/item/requests/ListProjectItemRequest";
import { ListProjectItemRequestOptions_BillingItem } from "../../../../services/soap/item/requests/ListProjectItemRequestOptions";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";
import { FPAData } from "../../../../infra/protected/FPA/FPAData";
import { UpdateProjectItemRequest } from "../../../../services/soap/item/requests/UpdateProjectItemRequest";
import { generateBillingItem } from "../../../../services/soap/item/requests/CreateProjectItemOptions";
import { ToastQueue } from '@adobe/react-spectrum';
import { GetItemRequest } from "../../../../services/soap/item/requests/GetItemRequest";
import { CreateProjectItemRequest } from "../../../../services/soap/item/requests/CreateProjectItemRequest";
import { DeleteProjectItemRequest } from "../../../../services/soap/item/requests/DeleteProjectItemRequest";
import useComonentReload from "../../../../hooks/UseComponentReload";
import { formatDecimalToString } from "../../../../utils/helperUtil";
import { useDeviceDetection } from "../../../../hooks/UseDeviceDetection";
import { useTableViewHeight } from "../../../../utils/tableViewUtils";
import { ListItemRequest } from "../../../../services/soap/item/requests/ListItemRequest";
import styles from "./BillingLineComponent.module.css";

interface BillingLineProps {
  isTotalPriceColumnVisible?: boolean;
  isUnitPriceColumnVisible?: boolean;
  minHeight?: number;
  percentWidth?: number;
  selectedItem?: FPAData;
  isNew?: boolean;
  setData?: (dataItems: any[]) => void;
}

export interface RowData {
  id: string;
  quantity: number;
  partID: string;
  _refId?: string;
  partNumber: string;
  productName: string;
  currency: string;
  unitPrice: number;
  amountWithVat: number;
  vatValue: number;
  totalPrice: string;
  totalPriceWithVat: string;
}

function BillingLine({
  isTotalPriceColumnVisible,
  isUnitPriceColumnVisible,
  selectedItem,
  isNew,
  setData,
}: Readonly<BillingLineProps>) {
  const { t } = useTranslation();
  let columnsData = [
    {
      name: t("quantity", { ns: "layout_components" }),
      key: "quantity",
      width: 100,
    },
    { name: t("part_number", { ns: "layout_components" }), key: "partNumber" },
    {
      name: t("product_name", { ns: "layout_components" }),
      key: "productName",
    },
    {
      name: t("unit_price", { ns: "layout_components" }),
      key: "unitPriceCurr",
    },
    {
      name: t("total_price", { ns: "layout_components" }),
      key: "totalPriceCurr",
    },
  ];

  let defaultRowData: RowData = {
    id: "0",
    quantity: 1,
    partID: "",
    _refId: "",
    partNumber: "",
    productName: "",
    currency: "",
    unitPrice: 0,
    amountWithVat: 0,
    vatValue: 0,
    totalPrice: "0",
    totalPriceWithVat: "0",
  };

  const [reloadComponent] = useComonentReload();
  const { itemService, store } = useDependency();
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const [billingLineData, setBillingLineData] = useState<any>([]);
  const [rowsData, setRowsData] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<RowData>(defaultRowData);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [showAddEditRow, setShowAddEditRow] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState<boolean>(false);

  // Use the utility for dynamic height calculations
  const { tableHeight, containerHeight, tableStyles } = useTableViewHeight(
    rowsData.length,
    {
      headerHeight: 50,
      rowHeight: 50,
      maxVisibleRows: 10,
      containerExtraSpace: 0, // Removed extra space to eliminate bottom gap
      maxContainerHeight: 550,
      showOverflow: false,
    }
  );

  let columnsDataMobile = [
    {
      name: t("quantity", { ns: "layout_components" }),
      key: "quantity",
      width: 100,
    },
    {
      name: t("part_number", { ns: "layout_components" }),
      key: "partNumber",
      width: 120,
    },
    {
      name: t("product_name", { ns: "layout_components" }),
      key: "productName",
      width: 250,
    },
    {
      name: t("unit_price", { ns: "layout_components" }),
      key: "unitPriceCurr",
      width: 120,
    },
    {
      name: t("total_price", { ns: "layout_components" }),
      key: "totalPriceCurr",
      width: deviceInfo.shouldUseMobileLayout() ? 250 : 120,
    },
  ];

  if (!isUnitPriceColumnVisible) {
    columnsData = columnsData.filter(
      (column) => column.key !== "unitPriceCurr"
    );
    columnsDataMobile = columnsDataMobile.filter(
      (column) => column.key !== "unitPriceCurr"
    );
  }

  if (!isTotalPriceColumnVisible) {
    columnsData = columnsData.filter(
      (column) => column.key !== "totalPriceCurr"
    );
    columnsDataMobile = columnsDataMobile.filter(
      (column) => column.key !== "totalPriceCurr"
    );
  }

  useEffect(() => {
    setShowAddEditRow(false);
    (async () => {
      setShowLoader(true);
      await loadBillingLineData();
      setShowLoader(false);
    })();
  }, [selectedItem, reloadComponent]);

  const updateBillingLineData = async (billingLineData: any[]) => {
    if (!billingLineData) return [];
    return Promise.all(
      billingLineData.map(async (item) => {
        const partNumber = await getPartNumber(item);
        return {
          ...item,
          _refId: `${partNumber}_${item.productName}_ref`,
          partNumber,
        };
      })
    );
  };

  // Function to load billing line data
  const loadBillingLineData = async () => {
    // Retrieve selected item
    //console.log('selectedItem', selectedItem);

    // Call API to fetch billing line data
    let billingRep: any = await itemService.listProjectItem(
      new ListProjectItemRequest(
        store.Server,
        store.SessionId,
        50,
        0,
        new ListProjectItemRequestOptions_BillingItem(String(selectedItem?.id))
      )
    );
    //  console.log('billingRep', billingRep);

    // Set billing line data state
    setBillingLineData(billingRep);

    // If API call is successful
    if (billingRep?.result === "OK") {
      // Map the billing line data to desired format
      let billingLineData = billingRep?.ROWS?.map((item: any) => {
        return {
          id: item.ITEM.id,
          quantity: formatDecimalToString(
            Number.parseFloat(item.ITEM.quantity).toFixed(2)
          ),
          partID: item.ITEM.item,
          _refId: "",
          partNumber: "Part Number",
          productName: item.ITEM.name,
          currency: item.ITEM.currency,
          vatValue: item.ITEM.vatValue,
          amountWithVat: item.ITEM.amountWithVat,
          unitPrice: formatDecimalToString(item.ITEM.amount),
          unitPriceCurr:
            formatDecimalToString(item.ITEM.amount) + " " + item.ITEM.currency,
          totalPrice: formatDecimalToString(
            Number.parseFloat(item.ITEM.totalAmount).toFixed(2)
          ),
          totalPriceCurr:
            formatDecimalToString(
              Number.parseFloat(item.ITEM.totalAmount).toFixed(2)
            ) +
            " " +
            item.ITEM.currency,
          purchasePrice: item.ITEM.purchasePrice,
          totalAmountWithVat: item.ITEM.totalAmountWithVat,
        };
      });

      // Update billing line data with additional information
      updateBillingLineData(billingLineData).then((updatedData: any) => {
        //console.log('updatedData', updatedData);
        setRowsData(updatedData);
        //console.log('rowsData', rowsData);
      });
    }
  };

  const getPartNumber = async (item: any) => {
    //console.log('item', item);
    let itemResp = await itemService.getItem(
      new GetItemRequest(store.Server, store.SessionId, item.partID)
    );
    //console.log('itemResp', itemResp);
    return itemResp.ITEM.refId;
  };

  const shouldUseModalLayout = () => {
    return deviceInfo.shouldUseMobileLayout() || deviceInfo.screenWidth <= 1440;
  };

  const openAddEditDialog = () => {
    shouldUseModalLayout() ? setIsOpen(true) : setShowAddEditRow(true);
  };

  const handleRowClick = (rowId: any): void => {
    //const isSelectedRowNotEmpty = selectedRow && Object.keys(selectedRow).length > 0;
    //console.log('rowId', rowId);
    const rowData: any = rowsData.find(
      (row: any) => row.id === rowId.currentKey
    );
    //console.log('rowData', rowData);
    setSelectedRow(rowData);
    openAddEditDialog();
    setSelectedKeys(rowId);
  };

  const handleRowClose = (): void => {
    setSelectedRow(defaultRowData);
    setShowAddEditRow(false);
    shouldUseModalLayout() && setIsOpen(false);
    handleUnselect();
  };

  const handleUnselect = () => {
    setSelectedKeys(new Set());
  };

  const addRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        const updatedRowsData = {
          ...record,
          id: Math.random().toString(36).substring(7),
          unitPriceCurr:
            formatDecimalToString(record.unitPrice) + " " + record.currency,
          totalPriceCurr:
            formatDecimalToString(
              Number.parseFloat(record.totalPrice).toFixed(2)
            ) +
            " " +
            record.currency,
        };

        // Add the new record to the updated rows data
        const newRowsData = [...rowsData, updatedRowsData];
        // console.log('newRowsData', newRowsData);
        // Update the state with the new rows data
        setRowsData(newRowsData);
        setData(newRowsData);
        handleRowClose();
        return;
      }
      setShowLoader(true);
      //console.log('record', record);

      let result = await itemService.createProjectItem(
        new CreateProjectItemRequest(
          store.Server,
          store.SessionId,
          generateBillingItem(
            String(selectedItem?.parent_id),
            String(selectedItem?.id),
            record.partID,
            record.productName,
            record.quantity,
            record.unitPrice,
            record.totalPrice,
            record.totalPriceWithVat || 0,
            record.currency,
            record.vatValue || 0,
            record.purchasePrice
          )
        )
      );

      // console.log('result', result);
      if (result.result === "OK") {
        await loadBillingLineData();
        handleRowClose();
        ToastQueue.positive("Record saved successfully", { timeout: 3000 });
      } else {
        ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
      }
    } catch (error: any) {
      ToastQueue.negative(error.message, { timeout: 3000 });
      throw error;
    } finally {
      setShowLoader(false);
    }
  };

  const updateRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        record.unitPriceCurr =
          formatDecimalToString(record.unitPrice) + " " + record.currency;
        record.totalPriceCurr =
          formatDecimalToString(
            Number.parseFloat(record.totalPrice).toFixed(2)
          ) +
          " " +
          record.currency;
        const updatedRowsData = rowsData.map((row: any) =>
          row.id === record.id ? record : row
        );
        //console.log('updatedRowsData', updatedRowsData);
        setRowsData(updatedRowsData);
        setData(updatedRowsData);
        handleRowClose();
        return;
      }
      setShowLoader(true);
      //console.log('update record', record);
      let itemRow = billingLineData.ROWS.find(
        (row: any) => row.ITEM.id == record.id
      );
      //console.log('itemRow', itemRow);
      let result = await itemService.updateProjectItem(
        new UpdateProjectItemRequest(
          store.Server,
          store.SessionId,
          generateBillingItem(
            String(selectedItem?.parent_id),
            String(selectedItem?.id),
            record.partID,
            record.productName,
            record.quantity,
            record.unitPrice,
            record.totalPrice,
            record.totalAmountWithVat || 0,
            record.currency,
            record.vatValue || 0,
            record.purchasePrice,
            itemRow.ITEM
          )
        )
      );

      // console.log('result', result);
      if (result.result === "OK") {
        await loadBillingLineData();
        handleRowClose();
        ToastQueue.positive(
          t("record_updated_successfully", { ns: "layout_components" }),
          { timeout: 3000 }
        );
      } else {
        ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
      }
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  const deleteRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        //console.log('record', record);
        const updatedRowsData = rowsData.filter(
          (row: any) => row.id !== record.id
        );
        //console.log('updatedRowsData', updatedRowsData);
        setRowsData(updatedRowsData);
        setData(updatedRowsData);
        handleRowClose();
        return;
      }
      setShowLoader(true);
      //console.log('record', record);

      let result = await itemService.deleteProjectItem(
        new DeleteProjectItemRequest(store.Server, store.SessionId, record.id)
      );

      // console.log('result', result);
      if (result.result === "OK") {
        await loadBillingLineData();
        handleRowClose();
        ToastQueue.positive("Record deleted successfully", { timeout: 3000 });
      } else {
        ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
      }
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const handleBarcodeSelect = async (barcode: string) => {
    try {
      setShowLoader(true);
      setIsBarcodeModalOpen(false);

      // Call listItem API with field9 parameter
      const itemResp: any = await itemService.listItem(
        new ListItemRequest(store.Server, store.SessionId, 10, 0, {
          field9: barcode,
        })
      );

      if (
        itemResp?.result === "OK" &&
        itemResp?.ITEM &&
        itemResp.ITEM.length > 0
      ) {
        // Item found - get the first result
        const foundItem = itemResp.ITEM[0];

        // Populate selectedRow with item data
        const newRowData: RowData = {
          id: "0", // New record
          quantity: 1,
          partID: foundItem.id,
          _refId: `${foundItem.refId}_${foundItem.name}_ref`,
          partNumber: foundItem.refId || "",
          productName: foundItem.name,
          currency: foundItem.currency || "",
          unitPrice: foundItem.amount || 0,
          amountWithVat: foundItem.amountWithVat || 0,
          vatValue: foundItem.vatValue || 0,
          totalPrice: String(foundItem.amount || 0),
          totalPriceWithVat: String(foundItem.amountWithVat || 0),
        };

        setSelectedRow(newRowData);
        handleUnselect();
        openAddEditDialog();

        ToastQueue.positive(
          t("item_found_by_barcode", { ns: "layout_components" }),
          { timeout: 3000 }
        );
      } else {
        // No item found
        ToastQueue.negative(
          t("no_item_found_barcode", { ns: "layout_components" }),
          { timeout: 5000 }
        );
        // Keep modal open for retry
        setIsBarcodeModalOpen(true);
      }
    } catch (error: any) {
      ToastQueue.negative(error.message || "Error searching for barcode", {
        timeout: 3000,
      });
      // Keep modal open for retry
      setIsBarcodeModalOpen(true);
    } finally {
      setShowLoader(false);
    }
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={"center"} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <View position={"relative"} width={"100%"}>
        {/* Table section */}
        <View position={"relative"} width={"100%"}>
          <Flex direction={"column"} gap={"size-150"} width={"100%"}>
            <Flex
              direction={"row"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={"heading_text_parent"}
            >
              <Content
                position={"relative"}
                UNSAFE_className={styles.heading_text}
              >
                {t("material_billing", { ns: "layout_components" })}
              </Content>
              <Flex
                direction={"row"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"size-200"}
                UNSAFE_className={styles.icon_add_parent}
              >
                <Content>
                  <Link isQuiet onPress={() => setIsBarcodeModalOpen(true)}>
                    <i className="bi bi-upc-scan fs-5">
                      <View
                        UNSAFE_className={styles.icon_add_text}
                        UNSAFE_style={{ paddingLeft: 5 }}
                      >
                        {t("scan_barcode", { ns: "layout_components" })}
                      </View>
                    </i>
                  </Link>
                </Content>
                <Content>
                  <Link
                    isQuiet
                    onPress={(e) => {
                      setSelectedRow(defaultRowData);
                      handleUnselect();
                      openAddEditDialog();
                    }}
                  >
                    <i className="bi bi-plus fs-5">
                      <View UNSAFE_className={styles.icon_add_text}>
                        {t("add_material", { ns: "layout_components" })}
                      </View>
                    </i>
                  </Link>
                </Content>
              </Flex>
            </Flex>
            <Flex
              direction={"column"}
              width={"100%"}
              UNSAFE_style={tableStyles}
            >
              <Flex height={containerHeight}>
                <TableView
                  UNSAFE_className="tblLayoutComponent"
                  aria-label="Billing Line"
                  onSelectionChange={handleRowClick}
                  selectionMode="single"
                  selectedKeys={selectedKeys}
                  selectionStyle="highlight"
                  width={"100%"}
                  height={tableHeight}
                  UNSAFE_style={{ position: "absolute" }}
                >
                  <TableHeader
                    columns={
                      deviceInfo.shouldUseMobileLayout()
                        ? columnsDataMobile
                        : columnsData
                    }
                  >
                    {(column) => (
                      <Column
                        showDivider
                        key={column.key}
                        width={column?.width}
                      >
                        {column.name}
                      </Column>
                    )}
                  </TableHeader>
                  <TableBody items={rowsData}>
                    {(item) => (
                      <Row>
                        {(columnKey) => (
                          <Cell>{(item as any)[String(columnKey)]}</Cell>
                        )}
                      </Row>
                    )}
                  </TableBody>
                </TableView>
              </Flex>
            </Flex>

            {/* Mobile Dialog Container inside absolute positioned area */}
            {shouldUseModalLayout() && (
              <DialogContainer
                isDismissable
                onDismiss={() => {
                  setIsOpen(false);
                }}
              >
                {isOpen && (
                  <Dialog id="dlgBillingLine">
                    <Heading>
                      {" "}
                      {selectedRow.id === "0"
                        ? t("add", { ns: "layout_components" })
                        : t("edit", { ns: "layout_components" })}{" "}
                      {t("material", { ns: "layout_components" })}
                    </Heading>
                    <Content>
                      <BillingLineAddEdit
                        id={String(selectedItem?.id)}
                        defaultRowData={defaultRowData}
                        selectedRowData={selectedRow}
                        handleRowClose={handleRowClose}
                        addRecord={addRecord}
                        updateRecord={updateRecord}
                        deleteRecord={deleteRecord}
                        isUnitPriceColumnVisible={isUnitPriceColumnVisible}
                        isTotalPriceColumnVisible={isTotalPriceColumnVisible}
                        closeDialog={closeDialog}
                        styles={styles}
                      />
                    </Content>
                  </Dialog>
                )}
              </DialogContainer>
            )}
          </Flex>
        </View>

        {/* BillingLineAddEdit section - OUTSIDE absolute positioning for proper document flow */}
        {!shouldUseModalLayout() && showAddEditRow && (
          <Flex
            direction={"column"}
            width={"100%"}
            marginTop={"size-200"}
            UNSAFE_style={{ zIndex: 2 }}
          >
            <BillingLineAddEdit
              id={String(selectedItem?.id)}
              defaultRowData={defaultRowData}
              selectedRowData={selectedRow}
              handleRowClose={handleRowClose}
              addRecord={addRecord}
              updateRecord={updateRecord}
              deleteRecord={deleteRecord}
              isUnitPriceColumnVisible={isUnitPriceColumnVisible}
              isTotalPriceColumnVisible={isTotalPriceColumnVisible}
              closeDialog={closeDialog}
              styles={styles}
            />
          </Flex>
        )}

        {/* Barcode Scanner Modal */}
        <BarcodeScannerModal
          isOpen={isBarcodeModalOpen}
          onClose={() => setIsBarcodeModalOpen(false)}
          onSelect={handleBarcodeSelect}
          isMobile={deviceInfo.shouldUseMobileLayout()}
        />
      </View>
    );
  }
}

export const BillingLineComponent = LayoutComponent(BillingLine);
